import React, { useState } from "react";
import { Button, Label, Input } from "@/components/ui";
import { CheckCircle, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface SummaryItem {
  label: string;
  value: React.ReactNode;
}

interface FormRequestSuccessViewProps {
  url: string | null | undefined;
  summaryItems: SummaryItem[];
}

export function FormRequestSuccessView({ url, summaryItems }: FormRequestSuccessViewProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex flex-col items-center py-4 px-2 animate-in fade-in zoom-in-95 duration-300">
      <div className="relative mb-6 select-none">
        <div className="absolute inset-0 scale-125 bg-emerald-100 rounded-full blur-2xl opacity-60 animate-pulse" />
        <div className="relative z-10 flex items-center justify-center size-16 bg-white rounded-full shadow-lg border border-emerald-100">
          <CheckCircle className="size-10 text-emerald-500" />
        </div>
      </div>

      <div className="text-center mb-6 select-none">
        <h3 className="text-lg font-bold text-slate-800 tracking-tight">Your Form Link is Ready!</h3>
        <p className="text-xs text-muted-foreground mt-1">
          The personalized feedback link has been generated successfully.
        </p>
      </div>

      {/* Summary Details */}
      <div className="w-full bg-slate-50 border border-slate-100 rounded-xl p-4 text-xs space-y-2 mb-6 font-sans select-none">
        <div className="font-bold text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-200/60 pb-1.5 mb-2.5">
          Link Assignment Summary
        </div>
        {summaryItems.map((item, idx) => (
          <div key={idx} className="flex justify-between gap-4">
            <span className="text-slate-500 font-medium shrink-0">{item.label}:</span>
            <span className="font-semibold text-slate-800 text-right truncate">{item.value}</span>
          </div>
        ))}
      </div>

      {/* Link Input & Copy CTA */}
      <div className="w-full flex flex-col gap-2">
        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide select-none">
          Copy Link to Share
        </Label>
        <div className="flex gap-2 w-full">
          <Input
            readOnly
            value={url || ""}
            className="flex-1 bg-slate-50 border-slate-200 text-xs font-mono select-all h-10 py-2.5 px-3 rounded-xl focus:ring-0 focus:border-slate-200"
          />
          <Button
            onClick={handleCopy}
            variant={copied ? "default" : "outline"}
            className={cn(
              "shrink-0 h-10 px-4 rounded-xl transition-all duration-200 select-none flex items-center gap-1.5",
              copied ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700" : ""
            )}
            type="button"
          >
            {copied ? (
              <>
                <Check className="size-4 shrink-0" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="size-4 shrink-0" />
                <span>Copy</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
